import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TextField,
  Autocomplete,
  Paper,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Radar from "radar-sdk-js";

interface RadarAddress {
  formattedAddress: string;
  latitude: number;
  longitude: number;
  addressLabel?: string;
  placeLabel?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface AutocompleteAddressProps {
  name: string;
  value: string;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddressSelect?: (address: RadarAddress) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
}

const AutocompleteAddress: React.FC<AutocompleteAddressProps> = ({
  name,
  value,
  label,
  onChange,
  onAddressSelect,
  error,
  placeholder,
  required = false,
}) => {
  const [addresses, setAddresses] = useState<RadarAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Get Radar API key from environment variables
  const RADAR_API_KEY = import.meta.env.VITE_RADAR_API_KEY;

  // Initialize Radar SDK
  useEffect(() => {
    if (RADAR_API_KEY) {
      Radar.initialize(RADAR_API_KEY);
    }
  }, [RADAR_API_KEY]);

  const searchAddresses = useCallback(
    async (query: string) => {
      if (!query.trim() || query.length < 3) {
        setAddresses([]);
        return;
      }

      if (!RADAR_API_KEY) {
        console.warn(
          "Radar API key not found. Please set VITE_RADAR_API_KEY environment variable.",
        );
        return;
      }

      setLoading(true);

      try {
        const result = await Radar.autocomplete({
          query: query,
          limit: 5,
        });

        if (result && result.addresses && Array.isArray(result.addresses)) {
          const formattedAddresses: RadarAddress[] = result.addresses.map(
            (address: any) => ({
              formattedAddress:
                address.formattedAddress || address.addressLabel || "",
              latitude: address.latitude || 0,
              longitude: address.longitude || 0,
              addressLabel: address.addressLabel,
              placeLabel: address.placeLabel,
              city: address.city,
              state: address.state,
              postalCode: address.postalCode,
              country: address.country,
            }),
          );

          setAddresses(formattedAddresses);
        } else {
          setAddresses([]);
        }
      } catch (error) {
        console.error("Error fetching addresses from Radar:", error);
        setAddresses([]);
      } finally {
        setLoading(false);
      }
    },
    [RADAR_API_KEY],
  );

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        searchAddresses(query);
      }, 500); // 0.5 seconds delay
    },
    [searchAddresses],
  );

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    setInputValue(newInputValue);

    // Create a synthetic event for the parent onChange handler
    const syntheticEvent = {
      target: {
        name,
        value: newInputValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
    debouncedSearch(newInputValue);
  };

  const handleAddressSelect = (
    event: React.SyntheticEvent,
    selectedAddress: RadarAddress | null,
  ) => {
    if (selectedAddress) {
      const addressValue = selectedAddress.formattedAddress;
      setInputValue(addressValue);

      // Update the parent component with the selected address
      const syntheticEvent = {
        target: {
          name,
          value: addressValue,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);

      // Call the address select callback if provided
      if (onAddressSelect) {
        onAddressSelect(selectedAddress);
      }

      // Clear suggestions after selection
      setAddresses([]);
    }
  };

  const renderOption = (props: any, address: RadarAddress) => {
    const { key, ...otherProps } = props;
    return (
      <Box
        component="li"
        key={key}
        {...otherProps}
        sx={{ display: "flex", alignItems: "center", py: 1 }}
      >
        <LocationOnIcon sx={{ mr: 2, color: "primary.main" }} />
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {address.placeLabel || address.addressLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {address.formattedAddress}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderInput = (params: any) => (
    <TextField
      {...params}
      name={name}
      label={label}
      placeholder={placeholder}
      required={required}
      error={!!error}
      helperText={error}
      sx={{ bgcolor: "background.paper" }}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {loading ? <CircularProgress color="inherit" size={20} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  );

  if (!RADAR_API_KEY) {
    // Fallback to regular text field if no API key
    return (
      <TextField
        fullWidth
        variant="outlined"
        type="text"
        name={name}
        value={inputValue}
        label={label}
        placeholder={placeholder}
        required={required}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange(e);
        }}
        helperText={
          error || "Radar API key not configured (VITE_RADAR_API_KEY)"
        }
        error={!!error}
        sx={{ bgcolor: "background.paper" }}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo
      options={addresses}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.formattedAddress
      }
      filterOptions={(x) => x} // Don't filter, we're handling this with the API
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleAddressSelect}
      renderInput={renderInput}
      renderOption={renderOption}
      PaperComponent={(props) => (
        <Paper {...props} sx={{ maxHeight: 300, overflow: "auto" }} />
      )}
      loading={loading}
      loadingText="Searching addresses..."
      noOptionsText={
        inputValue.length < 3
          ? "Type at least 3 characters"
          : "No addresses found"
      }
      clearOnBlur={false}
      selectOnFocus
      handleHomeEndKeys
    />
  );
};

export default AutocompleteAddress;
